"use client";
import { Building2, User, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useAssetImage } from "../../utils/AssetImageProvider";

export interface AvatarProps {
  /**
   * URL of the avatar image
   */
  src?: string;
  /**
   * Alternative text for the image
   */
  alt?: string;
  /**
   * First name for generating initials
   */
  firstName?: string;
  /**
   * Last name for generating initials
   */
  lastName?: string;
  /**
   * Full name (used as fallback for alt text)
   */
  fullName?: string;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Size of the avatar
   * @default "md"
   */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * Shape of the avatar
   * @default "circle"
   */
  shape?: "circle" | "square";
  /**
   * Visual variant
   * @default "user"
   */
  variant?: "user" | "team" | "organization";
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Status indicator
   */
  status?: "online" | "offline" | "away" | "busy";
  /**
   * Whether to show a border
   * @default false
   */
  bordered?: boolean;
  /**
   * Entity identifier for PII masking (e.g., "contact:ctc_abc123")
   * When provided, adds data-pii-* attributes for the Curtain extension
   */
  piiEntity?: string;
}

/**
 * Avatar
 *
 * Display user, team, or organization avatars with support for images,
 * initials, and fallback icons. Includes optional status indicators.
 */
export function Avatar({
  src: sourceSrc,
  alt,
  firstName,
  lastName,
  fullName,
  onClick,
  size = "md",
  shape = "circle",
  variant = "user",
  className,
  status,
  bordered = false,
  piiEntity,
}: AvatarProps) {
  const src = useAssetImage(sourceSrc);
  const [hasImageError, setHasImageError] = useState(false);
  const handleImageError = useCallback(() => setHasImageError(true), []);

  useEffect(() => {
    setHasImageError(false);
  }, [src]);

  const sizeClasses = {
    xs: "h-6 w-6 text-xs",
    sm: "h-8 w-8 text-sm",
    md: "h-10 w-10 text-base",
    lg: "h-12 w-12 text-lg",
    xl: "h-16 w-16 text-xl",
  };

  const statusSizeClasses = {
    xs: "h-1.5 w-1.5",
    sm: "h-2 w-2",
    md: "h-2.5 w-2.5",
    lg: "h-3 w-3",
    xl: "h-4 w-4",
  };

  const statusPositionClasses = {
    xs: "bottom-0 right-0",
    sm: "bottom-0 right-0",
    md: "bottom-0 right-0",
    lg: "bottom-0.5 right-0.5",
    xl: "bottom-1 right-1",
  };

  const statusColors = {
    online: "bg-feedback-success-background border-feedback-success-border",
    offline: "bg-text-secondary border-border-muted",
    away: "bg-feedback-warning-background border-feedback-warning-border",
    busy: "bg-feedback-error-background border-feedback-error-border",
  };

  const variantColors = {
    user: "bg-feedback-info-background text-feedback-info-text",
    team: "bg-feedback-info-background text-feedback-info-text",
    organization: "bg-feedback-success-background text-feedback-success-text",
  };

  const shapeClasses = shape === "circle" ? "rounded-full" : "rounded-md";

  const baseClasses = twMerge(
    "relative inline-flex items-center justify-center overflow-hidden transition-opacity outline-none",
    sizeClasses[size],
    shapeClasses,
    onClick && "cursor-pointer hover:opacity-80",
    bordered && "ring-2 ring-background-surface",
    className
  );

  // Generate initials from name
  const getInitials = () => {
    if (firstName || lastName) {
      const first = firstName?.[0] || "";
      const last = lastName?.[0] || "";
      return (first + last).toUpperCase();
    }
    if (fullName) {
      const parts = fullName.split(" ");
      const first = parts[0]?.[0] || "";
      const last = parts[parts.length - 1]?.[0] || "";
      return first === last ? first.toUpperCase() : (first + last).toUpperCase();
    }
    return "";
  };

  const initials = getInitials();

  // Get the appropriate icon for the variant
  const getIcon = () => {
    const iconSize = {
      xs: 14,
      sm: 16,
      md: 20,
      lg: 24,
      xl: 32,
    }[size];

    switch (variant) {
      case "team":
        return <Users size={iconSize} />;
      case "organization":
        return <Building2 size={iconSize} />;
      default:
        return <User size={iconSize} />;
    }
  };

  // Determine PII type based on variant
  const getPiiType = (isImage: boolean) => {
    if (!piiEntity) return undefined;
    if (variant === "organization") {
      // Organization variant: logo for images, initials for text fallback
      return isImage ? "organization-logo" : "initials";
    }
    return isImage ? "avatar" : "initials";
  };

  const renderAvatar = () => {
    // If src is provided and no error, render only the image
    if (src && !hasImageError) {
      return (
        <img
          src={src}
          alt={alt || fullName || "Avatar"}
          className="h-full w-full object-cover"
          onError={handleImageError}
          data-pii-type={getPiiType(true)}
          data-pii-entity={piiEntity}
        />
      );
    }

    // Show initials if available (when no src or image failed)
    if (initials) {
      return (
        <span className="font-semibold" data-pii-type={getPiiType(false)} data-pii-entity={piiEntity}>
          {initials}
        </span>
      );
    }

    // Show icon as fallback
    return getIcon();
  };

  return (
    <div className="relative inline-block">
      <div
        data-avatar
        className={twMerge(baseClasses, !src || hasImageError ? variantColors[variant] : "")}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        {renderAvatar()}
      </div>
      {status && (
        <span
          className={twMerge(
            "absolute border-2 border-background-surface",
            shapeClasses,
            statusSizeClasses[size],
            statusPositionClasses[size],
            statusColors[status]
          )}
          aria-label={`Status: ${status}`}
        />
      )}
    </div>
  );
}
